<template>
  <div>
    <v-dialog v-model="downgradeDialog" persistent max-width="430">
      <div class="ln-dialog">
        <h2 class="ln-dialog__title">Downgrade</h2>

        <div class="ln-dialog__body">
          <p>
            You are downgrading to the
            <strong class="text-capitalize">{{ planName }}</strong> plan, this
            may impact your active links, are you sure?
          </p>

          <!-- <iframe
            v-if="planName === 'free'"
            width="100%"
            height="320px"
            src="https://surveys.hotjar.com/93cfa597-e977-4be5-8772-b17c80cb7732"
            title="We need your help"
            frameborder="0"
          /> -->
          <template v-if="planName === 'free'">
            <v-divider></v-divider>
            <p class="ln-dialog__lead">
              Could you please let us know why you want to cancel your
              subscription?
            </p>
            <v-checkbox
              v-model="churnAnswers"
              density="compact"
              color="primary"
              label="Missing features I need"
              value="Missing features I need"
            ></v-checkbox>
            <v-checkbox
              v-model="churnAnswers"
              density="compact"
              color="primary"
              label="Technical issues"
              value="Technical issues"
            ></v-checkbox>
            <v-checkbox
              v-model="churnAnswers"
              density="compact"
              color="primary"
              label="Marketing campaign ended"
              value="Marketing campaign ended"
            ></v-checkbox>
            <v-checkbox
              density="compact"
              color="primary"
              v-model="churnAnswers"
              label="Too expensive"
              value="Too expensive"
            ></v-checkbox>
            <v-checkbox
              density="compact"
              color="primary"
              v-model="churnAnswers"
              label="Found an alternative service"
              value="Found an alternative service"
            ></v-checkbox>
            <v-checkbox
              v-model="churnAnswers"
              density="compact"
              color="primary"
              label="Other"
              value="Other"
            ></v-checkbox>

            <v-textarea
              v-model="churnOpenQuestion"
              color="primary"
              variant="outlined"
              label="Feedback"
            ></v-textarea>
          </template>
        </div>

        <div class="ln-dialog__actions">
          <button
            type="button"
            class="ln-btn ln-btn--quiet"
            @click="downgradeDialog = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ln-btn ln-btn--danger"
            @click="downgradePlanFinal()"
          >
            Confirm
          </button>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="430">
      <div class="ln-dialog">
        <h2 class="ln-dialog__title">{{ isRenew ? "Renew" : "Subscribe" }}</h2>

        <div class="ln-dialog__body" v-if="isRenew">
          <p>
            Your <strong class="text-capitalize">{{ planName }}</strong> plan is
            set to be cancelled.
          </p>
          <p>
            After clicking "Confirm" it will keep renewing as before, on the
            same billing date. There is nothing to pay now.
          </p>
        </div>

        <div class="ln-dialog__body" v-else>
          <p>
            You are subscribing to
            <strong class="text-capitalize">{{ planName }}</strong> plan.
          </p>
          <p>
            After clicking "Confirm" a secure popup from our payment processor
            "Paddle.com" will open, you will be able to enter your payments
            details and optionally your VAT tax number.
          </p>
        </div>

        <div class="ln-dialog__actions">
          <button
            type="button"
            class="ln-btn ln-btn--quiet"
            @click="dialog = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ln-btn ln-btn--solid"
            @click="upgradePlanFinal()"
          >
            Confirm
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- if same plan disable btn -->
    <button
      v-if="
        userPlan.plan.name == planName &&
        userPlan.user.paddle_cancellation_effective_date
      "
      type="button"
      class="ln-btn ln-btn--solid"
      :class="{ 'ln-btn--sm': smallBtn }"
      :disabled="isLoading"
      @click="upgradePlan()"
    >
      Renew
    </button>
    <button
      v-else-if="userPlan.plan.name == planName"
      type="button"
      class="ln-btn ln-btn--quiet ln-btn--sm"
      disabled
    >
      Current plan
    </button>
    <!-- allow to upgrade -->
    <button
      v-else-if="canUpgrade"
      type="button"
      class="ln-btn ln-btn--solid"
      :class="{ 'ln-btn--sm': smallBtn }"
      :disabled="isLoading"
      @click="upgradePlan()"
    >
      Upgrade
    </button>
    <!-- downgrade -->
    <button
      v-else
      type="button"
      class="ln-btn ln-btn--outline-danger"
      :class="{ 'ln-btn--sm': smallBtn }"
      :disabled="
        isLoading || userPlan.user.paddle_cancellation_effective_date !== null
      "
      @click="downgradePlan()"
    >
      Downgrade
    </button>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { movePlan, renewPlan } from "../api";
import { auth } from "../auth";
import { capture } from "../analytics";
import { getPaddleInstance } from "@paddle/paddle-js";

export default {
  props: ["planName", "smallBtn", "billingCycle"],
  data() {
    return {
      dialog: false,
      downgradeDialog: false,
      churnAnswers: [],
      churnOpenQuestion: "",
    };
  },
  computed: {
    ...mapState(["userPlan", "plans", "isLoading"]),
    ...mapGetters(["getUserEmail"]),
    canUpgrade() {
      return this.$store.getters.canUpgrade(this.planName);
    },
    planId() {
      for (const thisPlan of this.plans) {
        if (thisPlan.name === this.planName) {
          // For Paddle Billing, use price IDs based on billing cycle
          if (this.billingCycle === "yearly" && thisPlan.yearly) {
            return thisPlan.yearly.priceId;
          } else if (thisPlan.monthly) {
            return thisPlan.monthly.priceId;
          }
          // Fallback to activePaddleId for Classic or if no price IDs available
          return thisPlan.activePaddleId;
        }
      }
      return -1;
    },
    // Subscription still active, but already scheduled to be cancelled
    hasPendingCancellation() {
      return !!(
        this.userPlan.user.paddle_billing_subscription_id &&
        this.userPlan.user.paddle_cancellation_effective_date
      );
    },
    // Confirming keeps the current plan running, nothing is charged
    isRenew() {
      return (
        this.hasPendingCancellation && this.userPlan.plan.name === this.planName
      );
    },
    // Check if user has Paddle Billing subscription
    hasBillingSubscription() {
      return !!this.userPlan.user.paddle_billing_subscription_id;
    },
    // Check if user has Paddle Classic subscription
    hasClassicSubscription() {
      return !!this.userPlan.user.paddle_active_subscription_id;
    },
    // Determine which checkout flow to use
    useBillingCheckout() {
      return !this.hasClassicSubscription || this.hasBillingSubscription;
    },
  },
  methods: {
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async downgradePlan() {
      this.downgradeDialog = true;
    },
    async downgradePlanFinal() {
      this.downgradeDialog = false;
      this.$store.commit("setIsLoading", true);
      try {
        const res = await movePlan(this.planName, {
          churnAnswers: this.churnAnswers,
          churnOpenQuestion: this.churnOpenQuestion,
        });
        console.log("res", res.success);
        if (res.success && res.success === true) {
          await this.sleep(3000);
          window.alert("Plan downgraded successfully");
        } else {
          window.alert(
            "Error: failed to downgrade the plan, please try again later or contact support",
          );
        }
        await this.$store.dispatch("getPlans");
        await this.$store.dispatch("getUserPlan");
      } finally {
        this.$store.commit("setIsLoading", false);
      }
      // TODO: add toast after response?
    },
    upgradePlan() {
      this.dialog = true;
    },
    async upgradePlanFinal() {
      this.$store.commit("setIsLoading", true);
      this.dialog = false;

      try {
        // A pending cancellation keeps the subscription active until its effective date, and
        // Paddle rejects every other change until the scheduled cancel is removed. Remove it
        // first, so we never open a checkout that would create a second subscription.
        let renewed = false;
        if (this.hasPendingCancellation) {
          const renewRes = await renewPlan();
          if (renewRes.success !== true) {
            window.alert(
              "Error: failed to renew the plan, please try again later or contact support",
            );
            return;
          }

          renewed = true;

          // renewing the plan the user is already on is the whole job
          if (this.userPlan.plan.name === this.planName) {
            this.$store.dispatch("getPlans");
            this.$store.dispatch("getUserPlan");
            window.alert("Plan renewed successfully");
            return;
          }
        }

        // Handle existing subscription upgrades
        if (
          (this.userPlan.user.paddle_active_subscription_id &&
            !this.userPlan.user.paddle_cancellation_effective_date) ||
          this.userPlan.user.paddle_billing_subscription_id
        ) {
          if (
            this.billingCycle == "yearly" &&
            this.userPlan.user.paddle_active_subscription_id
          ) {
            window.alert(
              "You can't upgrade to yearly plan from your current subscription, please cancel your current plan (downgrade to free plan), wait for the cancellation to be effective and try again",
            );
            return;
          }
          const res = await movePlan(this.planName, {}, this.billingCycle);
          if (res.success && res.success === true) {
            this.$store.dispatch("getPlans");
            this.$store.dispatch("getUserPlan");
            window.alert("Plan upgraded successfully");
          } else if (renewed) {
            // cancelling again is the last thing this user should do now
            window.alert(
              "Error: your plan was renewed and stays active, but the plan change failed. Please try again or contact support",
            );
          } else {
            window.alert(
              "Error: Failed to upgrade plan, please try to cancel your current plan (downgrade to free plan) and try again",
            );
          }
          return;
        }

        // New subscription checkout
        const paddleUuid = this.userPlan.user.paddle_passthrough_id;
        const billingCustomerId = this.userPlan.user.paddle_billing_customer_id;
        const priceId = this.planId;

        // The email in the store is a snapshot taken at login, and it can change from
        // the Account page, so ask Firebase for the current one before billing it
        let email = this.getUserEmail;
        try {
          const firebaseUser = auth().currentUser;
          await firebaseUser.reload();
          email = firebaseUser.email;
        } catch (err) {
          console.error(err);
          // A dead session or a deleted account must not reach the checkout, or we
          // would take a payment for an account that can no longer be billed
          if (
            [
              "auth/user-token-expired",
              "auth/invalid-user-token",
              "auth/user-not-found",
              "auth/user-disabled",
            ].includes(err.code)
          ) {
            window.alert(
              "Error: your session has expired, please log in again and retry",
            );
            return;
          }
          // any other failure (network, timeout) keeps the email we already have
        }

        // Paddle Billing checkout flow
        const paddlePayload = {
          items: [
            {
              priceId: priceId,
              quantity: 1,
            },
          ],
          // An account that already has a Paddle customer stays on it: with the login
          // email Paddle would open a second customer whenever the billing email
          // differs, and the receipts and the billing email of the first one would
          // no longer be the ones the Plan page shows
          customer: billingCustomerId
            ? { id: billingCustomerId }
            : { email: email },
          customData: {
            passthrough: paddleUuid,
            billing_cycle: this.billingCycle, // Pass billing cycle for reference
            // Include Tolt referral if present
            ...(window.tolt_referral && {
              tolt_referral: window.tolt_referral,
            }),
          },
          settings: {
            successUrl: `${this.$store.state.config.APP_URL}/processing`,
          },
        };
        console.log("paddle payload", paddlePayload);

        const paddleInstance = getPaddleInstance("v1");

        // Handle close callback
        // const closeCallback = () => {
        //   try {
        //     this.$store.commit("setIsLoading", false);
        //   } catch (err) {}
        // };

        // Open Paddle Billing checkout
        try {
          paddleInstance.Checkout.open(paddlePayload);
          // the intent to pay: the payment itself reaches PostHog from the
          // Paddle webhook, as payment_completed
          capture("checkout_opened", {
            plan: this.planName,
            billing_cycle: this.billingCycle,
            current_plan: this.userPlan.plan.name,
          });
        } catch (err) {
          // Paddle throws on invalid input (e.g. missing email), and without this the
          // dialog would just close and the button would look broken
          console.error(err);
          window.alert(
            "Error: failed to open the checkout, please reload the page and try again or contact support",
          );
        }
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },
  },
};
</script>

<style scoped>
.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--danger {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-btn--danger:hover {
  background: #fbdfe1;
}

.ln-btn--outline-danger {
  border-color: var(--ln-red-fg);
  background: transparent;
  color: var(--ln-red-fg);
}

.ln-btn--outline-danger:hover {
  background: var(--ln-red-bg);
}

.ln-btn--sm {
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

/* ---------- dialog -------------------------------------------------------- */

.ln-dialog {
  padding: 28px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.ln-dialog__title {
  margin: 0 0 16px;
  font-family: sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-dialog__body p {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.ln-dialog__body p.ln-dialog__lead {
  margin-top: 12px;
}

.ln-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--ln-line);
}
</style>
